import { ApiResponse } from 'apisauce';
import { IGift } from '../types';
import { Env } from '../utils/env';
import { extractMessage } from './api-caller';
import API from './api';
import { TEXT_CONSTANTS } from '../constants';

const api = API.create(Env.REACT_APP_API_BASE);

export interface ChildSponsorshipProduct {
  product_id: string;
  variant_id: string;
  price: string;
  sku: string;
  title: string;
}

export interface ShopifyCartChild {
  id: string;
  name: string;
}

interface BaseShopifyCartPayload {
  variant_id: string;
  utm: string;
  ms: string;
}

export interface SpecificChildrenShopifyCartPayload extends BaseShopifyCartPayload {
  children: ShopifyCartChild[];
}

export interface BulkDonationShopifyCartPayload extends BaseShopifyCartPayload {
  bulk_donation_number: number;
  public_id: string;
}

export type CreateShopifyCartPayload =
  | SpecificChildrenShopifyCartPayload
  | BulkDonationShopifyCartPayload;

export interface ShopifyCartTrackingParams {
  utm: string;
  ms: string;
}

export const buildBulkShopifyCartPayload = (
  variantId: string,
  numberOfChildren: number,
  publicId: string,
  tracking: ShopifyCartTrackingParams
): BulkDonationShopifyCartPayload => ({
  variant_id: variantId,
  bulk_donation_number: numberOfChildren,
  public_id: publicId,
  utm: tracking.utm,
  ms: tracking.ms,
});

const getErrorMessage = (response: ApiResponse<unknown>) => {
  const message = extractMessage(
    response.data || {},
    response.problem || 'TIMEOUT_ERROR',
    response.status || 500,
    response.config?.url || ''
  );
  return message || TEXT_CONSTANTS.DEFAULT_API_ERROR;
};

export const getAttributionFromStorage = (): ShopifyCartTrackingParams => {
  const defaults: ShopifyCartTrackingParams = { utm: 'vat_web', ms: 'vat_web' };

  try {
    const storedData = localStorage.getItem('attributionData');
    if (!storedData) return defaults;

    const parsed = JSON.parse(storedData);
    return {
      utm: parsed.utm_campaign || parsed.utm_source || defaults.utm,
      ms: parsed.ms || defaults.ms,
    };
  } catch {
    return defaults;
  }
};

export const mapBasketToCartChildren = (basket: IGift[]): ShopifyCartChild[] =>
  basket
    .filter((item) => item?.child)
    .map((item) => ({
      id: String(item.child.id),
      name: item.child.firstName,
    }));

export const calculateBulkDonationTotal = (
  bulkDonationNumber: number,
  unitPrice: number
): string => (bulkDonationNumber * unitPrice).toFixed(2);

export const getChildSponsorshipProduct = async (): Promise<ChildSponsorshipProduct> => {
  const response = await api.get<ChildSponsorshipProduct>('/api/shopify/child-sponsorship-product');

  if (response.ok && response.data?.variant_id) {
    return response.data;
  }

  throw new Error(getErrorMessage(response));
};

export const createShopifyCart = async (payload: CreateShopifyCartPayload): Promise<string> => {
  const response = await api.post<{ success: boolean; checkout_url: string }>(
    '/api/shopify/cart/create',
    payload
  );

  if (response.ok && response.data?.checkout_url) {
    return response.data.checkout_url;
  }

  throw new Error(getErrorMessage(response));
};

export const resolveCheckoutUrl = async (
  basket: IGift[],
  variantId?: string | null,
  bulkDonation?: {
    numberOfChildren: number;
    publicId: string;
  }
): Promise<string> => {
  let resolvedVariantId = variantId;

  if (!resolvedVariantId) {
    const product = await getChildSponsorshipProduct();
    resolvedVariantId = product.variant_id;
  }

  const tracking = getAttributionFromStorage();

  if (bulkDonation) {
    if (bulkDonation.numberOfChildren < 1 || !bulkDonation.publicId) {
      throw new Error(TEXT_CONSTANTS.DEFAULT_API_ERROR);
    }

    return createShopifyCart(
      buildBulkShopifyCartPayload(
        resolvedVariantId,
        bulkDonation.numberOfChildren,
        bulkDonation.publicId,
        tracking
      )
    );
  }

  const children = mapBasketToCartChildren(basket);
  if (children.length === 0) {
    throw new Error(TEXT_CONSTANTS.DEFAULT_API_ERROR);
  }

  return createShopifyCart({
    variant_id: resolvedVariantId,
    children,
    utm: tracking.utm,
    ms: tracking.ms,
  });
};
